import { FunctionComponent, useEffect, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Typography
} from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getSystemProfileAsync, updateSystemProfileAsync } from '../../store/system-profile/service';
import { SystemProfile } from '../../types/entities';
import SelectTimeZoneControl from '../form-controls/SelectTimeZoneControl';
import DialogCloseIconButton from '../ui/DialogCloseIconButton';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const SystemProfileDialog: FunctionComponent<Props> = ({ isOpen, onClose }: Props) => {
  const { profile, loading, loaded, pending, error } = useAppSelector((gs) => gs.systemProfileState);
  const dispatch = useAppDispatch();

  const [savePressed, setSavePressed] = useState(false);

  useEffect(() => {
    if (!loaded) {
      dispatch(getSystemProfileAsync());
    }
  }, [loaded]);

  const [settingsForm, setSettingsForm] = useState<SystemProfile | undefined>();

  useEffect(() => {
    setSettingsForm(profile);
  }, [profile]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateProfileForm = (field: keyof SystemProfile, value: any) => {
    const copy = {
      ...settingsForm,
      [field]: value
    } as SystemProfile;
    setSettingsForm(copy);
  };

  const handleReloadSystemProfileBtnClick = () => {
    dispatch(getSystemProfileAsync());
  };

  const handleSaveBtnClick = () => {
    setSavePressed(true);
    if (settingsForm) {
      dispatch(updateSystemProfileAsync(settingsForm));
    }
  };

  useEffect(() => {
    if (savePressed && !pending && !error) {
      onClose(); // auto close
    }
  }, [savePressed, pending, error]);

  return (
    <Dialog fullWidth open={isOpen} maxWidth="sm" onClose={onClose}>
      <DialogTitle sx={{ pl: 2 }}>Settings</DialogTitle>
      <DialogCloseIconButton onClose={onClose} />
      <DialogContent sx={{ p: 0 }}>
        {settingsForm && (
          <>
            <Divider>
              <Typography variant="caption">GENERAL</Typography>
            </Divider>
            <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, gap: 1 }}>
              <SelectTimeZoneControl
                selected={settingsForm?.displayTimeZone}
                onSelect={(timeZone) => updateProfileForm('displayTimeZone', timeZone)}
              />
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      sx={{ ml: 1 }}
                      checked={settingsForm.spreadDefaultPeriods === 1}
                      onChange={() => updateProfileForm('spreadDefaultPeriods', settingsForm.spreadDefaultPeriods === 1 ? 0 : 1)}
                    />
                  }
                  label={<Typography variant="body2">Auto Refresh Dashboard (every 2 min)</Typography>}
                  sx={{ minWidth: '200px' }}
                />
              </FormControl>
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      sx={{ ml: 1 }}
                      checked={settingsForm.showFedCaledar}
                      onChange={() => updateProfileForm('showFedCaledar', !settingsForm.showFedCaledar)}
                    />
                  }
                  label={<Typography variant="body2">Show FED Status Bar</Typography>}
                  sx={{ minWidth: '200px' }}
                />
              </FormControl>
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      sx={{ ml: 1 }}
                      checked={settingsForm.showRegimeAnalytics}
                      onChange={() => updateProfileForm('showRegimeAnalytics', !settingsForm.showRegimeAnalytics)}
                    />
                  }
                  label={<Typography variant="body2">Show Regime Analytics</Typography>}
                  sx={{ minWidth: '200px' }}
                />
              </FormControl>
            </Box>
            <Divider>
              <Typography variant="caption">SCHEDULE</Typography>
            </Divider>
            <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, gap: 1 }}>
              <FormControl fullWidth>
                <TextField
                  name="interval"
                  size="small"
                  label="Repeat Interval (minutes)"
                  variant="outlined"
                  type="number"
                  value={settingsForm?.scheduleIntervalMinutes || 0}
                  onChange={(e) => updateProfileForm('scheduleIntervalMinutes', Number(e.target.value))}
                />
              </FormControl>
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      sx={{ ml: 1 }}
                      checked={settingsForm.initStaleOrderRepairOnStart}
                      onChange={() => updateProfileForm('initStaleOrderRepairOnStart', !settingsForm.initStaleOrderRepairOnStart)}
                    />
                  }
                  label={<Typography variant="body2">Init Stale-Order Repair Job On Start</Typography>}
                  sx={{ minWidth: '200px' }}
                />
              </FormControl>
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      sx={{ ml: 1 }}
                      checked={settingsForm.initRegimeAnalyticsOnStart}
                      onChange={() => updateProfileForm('initRegimeAnalyticsOnStart', !settingsForm.initRegimeAnalyticsOnStart)}
                    />
                  }
                  label={<Typography variant="body2">Init Regime Analytics Job On Start</Typography>}
                  sx={{ minWidth: '200px' }}
                />
              </FormControl>
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      sx={{ ml: 1 }}
                      checked={settingsForm.initVolatilityAlertOnStart}
                      onChange={() => updateProfileForm('initVolatilityAlertOnStart', !settingsForm.initVolatilityAlertOnStart)}
                    />
                  }
                  label={<Typography variant="body2">Init Volatility Alert Job On Start</Typography>}
                  sx={{ minWidth: '200px' }}
                />
              </FormControl>
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      sx={{ ml: 1 }}
                      checked={settingsForm.initPriceAlertOnStart}
                      onChange={() => updateProfileForm('initPriceAlertOnStart', !settingsForm.initPriceAlertOnStart)}
                    />
                  }
                  label={<Typography variant="body2">Init Price Alert Job On Start</Typography>}
                  sx={{ minWidth: '200px' }}
                />
              </FormControl>
            </Box>
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', width: '100%' }}>
          <Button variant="outlined" onClick={handleReloadSystemProfileBtnClick}>
            Reload {loading && <CircularProgress sx={{ ml: 1 }} color="inherit" size="1rem" />}
          </Button>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" onClick={handleSaveBtnClick}>
              Save {pending && <CircularProgress sx={{ ml: 1 }} color="inherit" size="1rem" />}
            </Button>
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SystemProfileDialog;
